import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Upload, FileText, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface DXFUploaderProps {
  onSuccess: (data: any) => void;
  onError?: (error: string) => void;
  fileType?: 'dxf' | 'xml' | 'both';
}

export const DXFUploader = ({ onSuccess, onError, fileType = 'both' }: DXFUploaderProps) => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [fileName, setFileName] = useState<string>('');

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const extension = fileName.split('.').pop();

    // Validate file type
    const validExtensions = fileType === 'both' ? ['dxf', 'xml'] : [fileType];
    if (!validExtensions.includes(extension || '')) {
      const errorMsg = `Please upload a valid ${validExtensions.join(' or ').toUpperCase()} file`;
      toast({
        title: "Invalid file type",
        description: errorMsg,
        variant: "destructive"
      });
      onError?.(errorMsg);
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      const errorMsg = 'File too large. Maximum size is 10MB.';
      toast({
        title: "File too large",
        description: errorMsg,
        variant: "destructive"
      });
      onError?.(errorMsg);
      return;
    }

    setFileName(file.name);
    setUploading(true);
    setUploadStatus('idle');

    try {
      let data, error;

      if (extension === 'xml') {
        // Parse XML file
        const xmlContent = await file.text();
        const result = await supabase.functions.invoke('parse-gaf-xml', {
          body: { xmlContent }
        });
        data = result.data;
        error = result.error;
      } else {
        // Parse DXF file
        const formData = new FormData();
        formData.append('file', file);
        const result = await supabase.functions.invoke('parse-dxf', {
          body: formData
        });
        data = result.data;
        error = result.error;
      }

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || `Failed to parse ${extension?.toUpperCase()} file`);
      }

      setUploadStatus('success');
      
      // Different success messages for XML vs DXF
      if (extension === 'xml') {
        toast({
          title: "XML parsed successfully",
          description: `Found ${data.data.measurements.area_sqft} sq ft, ${data.data.diagnostics.facetCount} facets`
        });
      } else {
        toast({
          title: "DXF parsed successfully",
          description: `Found ${data.data.summary?.squares || 0} squares, ${data.data.lengthsLF?.ridge?.toFixed(1) || 0} LF ridge`
        });
      }
      
      onSuccess(data.data);

    } catch (error) {
      console.error('File upload error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to parse file';
      setUploadStatus('error');
      toast({
        title: "Failed to parse file",
        description: errorMsg,
        variant: "destructive"
      });
      onError?.(errorMsg);
    } finally {
      setUploading(false);
    }
  }, [onSuccess, onError, fileType]);

  const acceptedFileTypes = fileType === 'xml' 
    ? { 'text/xml': ['.xml'], 'application/xml': ['.xml'] }
    : fileType === 'dxf'
    ? { 'application/dxf': ['.dxf'], 'image/vnd.dxf': ['.dxf'] }
    : { 
        'application/dxf': ['.dxf'], 
        'image/vnd.dxf': ['.dxf'],
        'text/xml': ['.xml'],
        'application/xml': ['.xml']
      };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxFiles: 1,
    disabled: uploading
  });

  return (
    <Card className="p-6">
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
          ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        
        <div className="flex flex-col items-center gap-4">
          {uploadStatus === 'idle' && !uploading && (
            <>
              <Upload className="h-12 w-12 text-muted-foreground" />
              <div>
                <p className="text-lg font-medium">
                  {isDragActive 
                    ? `Drop ${fileType === 'both' ? 'DXF or XML' : fileType.toUpperCase()} file here` 
                    : `Drag & drop ${fileType === 'both' ? 'DXF or XML' : fileType.toUpperCase()} file here`
                  }
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  or click to browse (max 10MB)
                </p>
                {fileType === 'both' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Supports GAF QuickMeasure XML and DXF files
                  </p>
                )}
              </div>
            </>
          )}

          {uploading && (
            <>
              <FileText className="h-12 w-12 text-primary animate-pulse" />
              <div>
                <p className="text-lg font-medium">Parsing file...</p>
                <p className="text-sm text-muted-foreground mt-1">{fileName}</p>
              </div>
            </>
          )}

          {uploadStatus === 'success' && !uploading && (
            <>
              <CheckCircle2 className="h-12 w-12 text-green-500" />
              <div>
                <p className="text-lg font-medium text-green-600">Successfully parsed!</p>
                <p className="text-sm text-muted-foreground mt-1">{fileName}</p>
              </div>
            </>
          )}

          {uploadStatus === 'error' && !uploading && (
            <>
              <AlertCircle className="h-12 w-12 text-destructive" />
              <div>
                <p className="text-lg font-medium text-destructive">Parse failed</p>
                <p className="text-sm text-muted-foreground mt-1">Click to try again</p>
              </div>
            </>
          )}
        </div>
      </div>

      {uploadStatus === 'success' && (
        <Button
          onClick={() => setUploadStatus('idle')}
          variant="outline"
          className="w-full mt-4"
        >
          Upload Different File
        </Button>
      )}
    </Card>
  );
};
